import { AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { Account, JiraProject } from '@/types';

export interface ValidationCredentials {
  [key: string]: string;
}

export interface ValidateIntegrationErrorResponse {
  error: string;
  templates: {
    [key: string]: {
      error: string;
      missingPermissions: string[];
      projectKey: string;
      projectName: string;
    };
  };
  valid: boolean;
}

export interface ValidateIntegrationOptions {
  suppressErrorToast?: boolean;
}

export function useValidateIntegration() {
  const [isValidating, setIsValidating] = useState(false);
  const axios = useAxios();

  const validateIntegration = useCallback(
    async ({
      provider,
      account,
      config,
      settings,
      templateId,
      suppressErrorToast = false,
    }: {
      provider?: string;
      account?: string;
      config?: Account['config'];
      settings?: Account['settings'];
      templateId?: string;
    } & ValidateIntegrationOptions): Promise<{
      projects: JiraProject[];
      isValid: boolean;
      templatesError?: ValidateIntegrationErrorResponse['templates'];
    }> => {
      if (!provider) {
        return { projects: [], isValid: false };
      }

      setIsValidating(true);
      try {
        const response = await axios.post('/integration/validate', {
          provider,
          account,
          secret: account ? undefined : config,
          settings,
        });
        return { projects: response.data.projects, isValid: true };
      } catch (error) {
        let errorMessage = 'Failed to validate credentials';
        let errorDescription = 'An unexpected error occurred';
        let templatesError: ValidateIntegrationErrorResponse['templates'] = {};

        if (error instanceof Error) {
          if (error instanceof AxiosError) {
            const responseData: ValidateIntegrationErrorResponse =
              error?.response?.data ?? '';
            const { error: errorResponse, templates = {} } = responseData;
            templatesError = templates;
            errorDescription = errorResponse;
            if (Object.entries(templates).length) {
              errorMessage = 'Missing required permissions';
            } else {
              errorMessage = 'Invalid Credentials';
            }
          } else {
            errorMessage = 'Unexpected Error';
            errorDescription =
              'An unknown error occurred while validating credentials';
          }
        }

        const showError = templateId ? !!templatesError[templateId] : true;

        if (showError && !suppressErrorToast) {
          toast.error(errorMessage, {
            description: errorDescription,
          });
        }

        return { projects: [], templatesError, isValid: false };
      } finally {
        setIsValidating(false);
      }
    },
    [axios]
  );

  return {
    validateIntegration,
    isValidating,
  };
}

export default useValidateIntegration;
